import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Toaster, toast } from 'sonner';
import { Star, ArrowLeft, CircleNotch, Target, CaretUp, CaretDown, Lightning } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { ExportButton } from '../components/ExportCSV';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Window12Consecutive() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [sigData, setSigData] = useState(null);
  const [pairSort, setPairSort] = useState({ key: 'count', dir: 'desc' });

  useEffect(() => {
    (async () => {
      try {
        const [consRes, sigRes] = await Promise.all([
          axios.get(`${API}/analysis/consecutive`),
          axios.get(`${API}/analysis/signals`).catch(() => ({ data: null })),
        ]);
        setData(consRes.data);
        setSigData(sigRes.data);
      }
      catch { toast.error('Failed to fetch consecutive analysis'); }
      finally { setLoading(false); }
    })();
  }, []);

  const handlePairSort = (key) => setPairSort(prev => ({ key, dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc' }));
  const SortIcon = ({ k }) => pairSort.key !== k ? null : pairSort.dir === 'desc' ? <CaretDown size={10} /> : <CaretUp size={10} />;

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-btn"><ArrowLeft size={18} /></Button>
              <div className="w-10 h-10 bg-[#002FA7] rounded flex items-center justify-center"><Star size={24} weight="fill" className="text-amber-400" /></div>
              <div><h1 className="text-2xl font-bold text-gray-900">Window 12</h1><p className="text-xs text-gray-500">Consecutive Number Analysis</p></div>
            </div>
          </div>

            <ExportButton data={data?.top_pairs} filename="w12_consecutive_pairs" />
          </div>
      </header>
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading ? <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div> : data ? (
          <Tabs defaultValue="overview" className="space-y-4">
            <TabsList className="bg-white border border-gray-200 p-1">
              <TabsTrigger value="overview" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="pairs" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-pairs">Top Pairs</TabsTrigger>
              <TabsTrigger value="recent" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-recent">Last 20 Draws</TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              {/* Pair Shift Signal — VERY HIGH (from Signals) */}
              {sigData?.pair_shift_signal?.predictions?.length > 0 && (
                <div className="bg-white border-2 border-red-400 rounded-lg overflow-hidden mb-6" data-testid="w12-pair-shift-signal">
                  <div className="p-4 border-b border-red-300 bg-gradient-to-r from-red-600 to-rose-600 text-white">
                    <div className="flex items-center gap-2">
                      <Lightning size={20} weight="fill" />
                      <h3 className="font-bold">Consecutive Pair Shift — VERY HIGH SIGNAL</h3>
                      <span className="ml-auto text-xs text-white/70">Missing decades: {sigData.pair_shift_signal.missing_decades?.join(', ') || 'None'}</span>
                    </div>
                  </div>
                  <div className="p-4">
                    {/* Shift Chain */}
                    {sigData.pair_shift_signal.shifts?.length > 0 && (
                      <div className="mb-4">
                        <p className="text-[10px] font-bold text-gray-500 uppercase mb-1">Recent Pair Shift Chain</p>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {sigData.pair_shift_signal.shifts.map((s, i) => (
                            <div key={i} className="flex items-center gap-0.5">
                              <span className="bg-indigo-100 text-indigo-800 rounded-full px-2 py-0.5 text-[10px] font-bold font-mono">{s.from_pair[0]},{s.from_pair[1]}</span>
                              <span className={`text-[9px] font-bold px-1 rounded ${s.shift < 0 ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-700'}`}>{s.shift > 0 ? '+' : ''}{s.shift}</span>
                              {i === sigData.pair_shift_signal.shifts.length - 1 && (
                                <span className="bg-indigo-100 text-indigo-800 rounded-full px-2 py-0.5 text-[10px] font-bold font-mono">{s.to_pair[0]},{s.to_pair[1]}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Predictions */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {sigData.pair_shift_signal.predictions.slice(0, 6).map((pred, i) => (
                        <div key={i} className={`rounded-lg border-2 p-3 ${pred.decade_missing ? 'border-red-400 bg-red-50' : 'border-amber-300 bg-amber-50'}`}>
                          <div className="flex items-center gap-2 mb-1.5">
                            <span className="inline-flex w-9 h-9 rounded-full bg-red-600 text-white items-center justify-center font-mono font-bold">{pred.predicted_pair[0]}</span>
                            <span className="inline-flex w-9 h-9 rounded-full bg-red-600 text-white items-center justify-center font-mono font-bold">{pred.predicted_pair[1]}</span>
                            <div>
                              {pred.decade_missing && <p className="text-[10px] font-bold text-red-700">Decade {pred.predicted_decade} MISSING</p>}
                              <p className="text-[9px] text-gray-400">{pred.proof_paths?.length || 0} proof paths</p>
                            </div>
                          </div>
                          <div className="space-y-0.5 border-t border-red-200/60 pt-1.5">
                            {(pred.proof_paths || []).slice(0, 4).map((pp, pi) => (
                              <div key={pi} className="text-[10px] text-gray-600">
                                <span className="font-mono font-bold">{pp.from_pair[0]},{pp.from_pair[1]}</span>
                                <span className={`mx-0.5 font-bold ${pp.shift < 0 ? 'text-red-600' : 'text-emerald-700'}`}>{pp.shift > 0 ? '+' : ''}{pp.shift}</span>
                                <span className="font-mono">= {pred.predicted_pair[0]},{pred.predicted_pair[1]}</span>
                                {pp.proven_by && <span className="text-[9px] text-gray-400 ml-0.5">({pp.proven_by.split(' | ')[0]})</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Stats cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white border rounded p-4">
                  <p className="text-xs uppercase font-bold text-gray-500">Total Draws</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1" data-testid="stat-total">{data.total_draws}</p>
                </div>
                <div className="bg-white border rounded p-4 bg-amber-50 border-amber-200">
                  <p className="text-xs uppercase font-bold text-amber-600">Draws with Consecutive</p>
                  <p className="text-2xl font-bold text-amber-700 mt-1" data-testid="stat-with-consec">{data.draws_with_consec} ({data.draws_with_consec_pct}%)</p>
                </div>
                <div className="bg-white border rounded p-4">
                  <p className="text-xs uppercase font-bold text-gray-500">Without Consecutive</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{data.total_draws - data.draws_with_consec}</p>
                </div>
                <div className="bg-white border rounded p-4">
                  <p className="text-xs uppercase font-bold text-gray-500">Unique Consec Pairs</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{data.top_pairs.length}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Consecutive pairs per draw distribution */}
                <div className="bg-white border rounded p-4">
                  <h3 className="font-bold text-gray-900 mb-4">Consecutive Pairs per Draw</h3>
                  <p className="text-xs text-gray-500 mb-3">How many consecutive number pairs appear in each draw</p>
                  <div className="space-y-2" data-testid="pair-distribution">
                    {data.pair_distribution.map(r => {
                      const maxCount = Math.max(...data.pair_distribution.map(x => x.count));
                      return (
                        <div key={r.pairs} className="flex items-center gap-2">
                          <span className="font-mono text-sm w-20 text-right font-bold">{r.pairs} pair{r.pairs !== 1 ? 's' : ''}</span>
                          <div className="flex-1 bg-gray-100 rounded h-6 overflow-hidden relative">
                            <div className={`h-full rounded transition-all ${r.pairs === 0 ? 'bg-gray-400' : r.pairs === 1 ? 'bg-blue-500' : 'bg-[#002FA7]'}`} style={{ width: `${(r.count / maxCount) * 100}%` }} />
                            <span className="absolute right-2 top-0.5 text-[10px] font-mono font-bold">{r.count}x</span>
                          </div>
                          <span className="font-mono text-xs w-14 text-right font-bold">{r.pct}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Cluster distribution */}
                <div className="bg-white border rounded p-4">
                  <h3 className="font-bold text-gray-900 mb-4">Cluster Size Distribution</h3>
                  <p className="text-xs text-gray-500 mb-3">Max group of numbers within 3 of each other in a draw</p>
                  <div className="space-y-2" data-testid="cluster-distribution">
                    {data.cluster_distribution.map(r => {
                      const maxCount = Math.max(...data.cluster_distribution.map(x => x.count));
                      return (
                        <div key={r.cluster_size} className="flex items-center gap-2">
                          <span className="font-mono text-sm w-20 text-right font-bold">Size {r.cluster_size}</span>
                          <div className="flex-1 bg-gray-100 rounded h-6 overflow-hidden relative">
                            <div className={`h-full rounded transition-all ${r.cluster_size <= 2 ? 'bg-emerald-500' : r.cluster_size <= 3 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${(r.count / maxCount) * 100}%` }} />
                            <span className="absolute right-2 top-0.5 text-[10px] font-mono font-bold">{r.count}x</span>
                          </div>
                          <span className="font-mono text-xs w-14 text-right font-bold">{r.pct}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Prediction section */}
              <div className="bg-gradient-to-r from-indigo-700 to-blue-800 rounded-lg p-5 mt-6 text-white">
                <div className="flex items-center gap-2 mb-2"><Target size={24} weight="bold" /><h3 className="font-bold text-xl">Consecutive Pattern Insight</h3></div>
                <p className="text-xs text-white/70 mb-4">Based on {data.total_draws} draws: {data.draws_with_consec_pct}% contain at least one consecutive pair</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/10 rounded-lg p-4">
                    <h4 className="font-bold text-sm mb-2">Most Likely Scenario</h4>
                    {data.pair_distribution.sort((a, b) => b.pct - a.pct).slice(0, 1).map(p => (
                      <div key={p.pairs} className="text-center">
                        <span className="text-3xl font-mono font-bold">{p.pairs}</span>
                        <p className="text-xs mt-1 text-white/80">consecutive pair{p.pairs !== 1 ? 's' : ''} ({p.pct}% of draws)</p>
                      </div>
                    ))}
                  </div>
                  <div className="bg-white/10 rounded-lg p-4">
                    <h4 className="font-bold text-sm mb-2">Top 3 Consecutive Pairs</h4>
                    {data.top_pairs.slice(0, 3).map((p, idx) => (
                      <div key={p.pair} className={`flex justify-between items-center py-1 ${idx === 0 ? 'text-yellow-300 font-bold' : ''}`}>
                        <span className="font-mono">{p.pair}</span>
                        <span className="text-xs">{p.count}x ({p.pct}%)</span>
                      </div>
                    ))}
                  </div>
                  <div className="bg-white/10 rounded-lg p-4">
                    <h4 className="font-bold text-sm mb-2">Most Common Cluster</h4>
                    {data.cluster_distribution.sort((a, b) => b.count - a.count).slice(0, 1).map(c => (
                      <div key={c.cluster_size} className="text-center">
                        <span className="text-3xl font-mono font-bold">{c.cluster_size}</span>
                        <p className="text-xs mt-1 text-white/80">numbers within 3 of each other ({c.pct}%)</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="pairs">
              <div className="bg-white border rounded p-4">
                <h3 className="font-bold text-gray-900 mb-3">Top 30 Consecutive Pairs (sorted by frequency)</h3>
                <div className="overflow-x-auto" data-testid="top-pairs-table">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-2 text-left">#</th>
                        <th className="px-3 py-2 text-left cursor-pointer hover:bg-gray-200" onClick={() => handlePairSort('pair')}>Pair <SortIcon k="pair" /></th>
                        <th className="px-3 py-2 text-center cursor-pointer hover:bg-gray-200" onClick={() => handlePairSort('count')}>Count <SortIcon k="count" /></th>
                        <th className="px-3 py-2 text-center cursor-pointer hover:bg-gray-200" onClick={() => handlePairSort('pct')}>% <SortIcon k="pct" /></th>
                        <th className="px-3 py-2">Frequency</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...data.top_pairs].sort((a, b) => {
                        let aV = a[pairSort.key], bV = b[pairSort.key];
                        if (typeof aV === 'string') return pairSort.dir === 'desc' ? bV.localeCompare(aV) : aV.localeCompare(bV);
                        return pairSort.dir === 'desc' ? bV - aV : aV - bV;
                      }).map((p, idx) => (
                        <tr key={p.pair} className={idx < 5 ? 'bg-blue-100' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} style={idx < 5 ? {borderLeft: '4px solid #2563eb', backgroundColor: 'rgba(37, 99, 235, 0.18)'} : {}} data-testid={`pair-row-${idx}`}>
                          <td className="px-3 py-1.5 border-b font-bold text-gray-400">{idx + 1}</td>
                          <td className="px-3 py-1.5 border-b">
                            <span className="font-mono font-bold text-sm bg-[#002FA7]/10 text-[#002FA7] px-2 py-0.5 rounded">{p.pair}</span>
                          </td>
                          <td className="px-3 py-1.5 border-b font-mono text-center font-bold">{p.count}</td>
                          <td className="px-3 py-1.5 border-b font-mono text-center">{p.pct}%</td>
                          <td className="px-3 py-1.5 border-b">
                            <div className="bg-gray-100 rounded h-4 overflow-hidden">
                              <div className="h-full bg-[#002FA7] rounded" style={{ width: `${(p.count / data.top_pairs[0].count) * 100}%` }} />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="recent">
              <div className="bg-white border rounded p-4">
                <h3 className="font-bold text-gray-900 mb-3">{data.current_year} Draws ({data.cy_draws_count}) — Consecutive Pairs & Clusters</h3>
                <div className="overflow-x-auto" data-testid="recent-draws-table">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-2 text-center">Draw</th>
                        <th className="px-3 py-2 text-center">Date</th>
                        <th className="px-3 py-2 text-center">Balls (sorted)</th>
                        <th className="px-3 py-2 text-center">Consec Pairs</th>
                        <th className="px-3 py-2 text-center">Pairs Found</th>
                        <th className="px-3 py-2 text-center">Max Cluster</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.last_draws || data.last_20 || []).map((d, idx) => (
                        <tr key={d.draw} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`recent-row-${d.draw}`}>
                          <td className="px-3 py-1.5 border-b font-mono text-center">#{d.draw}</td>
                          <td className="px-3 py-1.5 border-b text-center">{d.date}</td>
                          <td className="px-3 py-1.5 border-b font-mono text-center">
                            <div className="flex items-center justify-center gap-1">
                              {d.balls.map((b, i) => {
                                const isConsec = d.pairs.some(p => {
                                  const [a, c] = p.split(',').map(Number);
                                  return b === a || b === c;
                                });
                                return (
                                  <span key={i} className={`inline-block w-7 h-7 rounded-full text-center leading-7 text-xs font-bold ${isConsec ? 'bg-[#002FA7] text-white' : 'bg-gray-100 text-gray-700'}`}>
                                    {b}
                                  </span>
                                );
                              })}
                            </div>
                          </td>
                          <td className={`px-3 py-1.5 border-b font-mono text-center font-bold text-lg ${d.consec_pairs > 0 ? 'text-[#002FA7]' : 'text-gray-300'}`}>{d.consec_pairs}</td>
                          <td className="px-3 py-1.5 border-b text-center">
                            {d.pairs.length > 0 ? d.pairs.map(p => (
                              <span key={p} className="inline-block bg-blue-100 text-blue-800 font-mono text-[10px] font-bold px-1.5 py-0.5 rounded mr-1">{p}</span>
                            )) : <span className="text-gray-300">-</span>}
                          </td>
                          <td className={`px-3 py-1.5 border-b font-mono text-center font-bold ${d.max_cluster >= 3 ? 'text-amber-600' : 'text-gray-500'}`}>{d.max_cluster}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        ) : null}
      </main>
    </div>
  );
}
